import { useEffect, useMemo, useState } from "react";
import DashboardShell from "../components/shell/DashboardShell";
import api, { formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Trash2, Pencil, Calculator, X, AlertTriangle, Clock, Zap, DollarSign, TrendingUp, Users, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import RefinanceModeler from "../components/RefinanceModeler";
import LoanPaymentsDialog from "../components/LoanPaymentsDialog";
import LoanStressTestDialog from "../components/loans/LoanStressTestDialog";
import MaturityAlertPanel from "../components/loans/MaturityAlertPanel";
import LoansAnalyticsPanel from "../components/loans/LoansAnalyticsPanel";
import BorrowersDialog from "../components/BorrowersDialog";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { Landmark } from "lucide-react";
import USPhoneInput from "../components/USPhoneInput";
import USDateInput from "../components/USDateInput";
import { formatUSD, parseUSD } from "../lib/currency";

const currency = (n) =>
  `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`;

/* ---------- Maturity helpers (client-side mirror of backend) ---------- */
function addMonths(date, months) {
  const d = new Date(date);
  const target = d.getMonth() + months;
  d.setMonth(target);
  return d;
}
function daysBetween(a, b) {
  const ms = b.setHours(0, 0, 0, 0) - a.setHours(0, 0, 0, 0);
  return Math.round(ms / 86400000);
}
function maturityInfo(loan) {
  if (!loan.loan_date) return null;
  // Honor explicit override (set on the Edit Loan form) — falls back to
  // computed `loan_date + term*12 + term_months` when no override is saved.
  let end;
  if (loan.maturity_date) {
    end = new Date(loan.maturity_date + "T00:00:00");
    if (Number.isNaN(end.getTime())) end = null;
  }
  if (!end) {
    const start = new Date(loan.loan_date);
    end = addMonths(start, loan.term_years * 12 + (Number(loan.term_months) || 0));
  }
  const today = new Date();
  const days = daysBetween(new Date(today), end);
  return { maturity_date: end.toISOString().slice(0, 10), days_until: days };
}
function ioTransitionInfo(loan) {
  if (loan.loan_type !== "interest_only" || !loan.io_period_months) return null;
  const start = new Date(loan.loan_date);
  const end = addMonths(start, Number(loan.io_period_months));
  const today = new Date();
  const days = daysBetween(new Date(today), end);
  return { transition_date: end.toISOString().slice(0, 10), days_until: days };
}
function severityFor(days) {
  if (days < 0 || days > 90) return null;
  if (days <= 30) return "red";
  if (days <= 60) return "yellow";
  return "green";
}
const SEVERITY_STYLES = {
  red:    { bg: "bg-red-50",    border: "border-red-300",    text: "text-red-700",    dot: "bg-red-500",    label: "DUE ≤30D" },
  yellow: { bg: "bg-amber-50",  border: "border-amber-300",  text: "text-amber-700",  dot: "bg-amber-500",  label: "DUE ≤60D" },
  green:  { bg: "bg-green-50",  border: "border-green-300",  text: "text-green-700",  dot: "bg-green-500",  label: "DUE ≤90D" },
};

function defaultForm() {
  return {
    loan_number: "",
    property_id: "",
    property_address: "",
    loan_date: new Date().toISOString().slice(0, 10),
    loan_amount: 1000000,
    interest_rate: 6.5,
    term_years: 25,
    loan_type: "amortizing",
    io_period_months: 0,
    borrower_name: "",
    borrower_email: "",
    borrower_phone: "",
    borrower_id: "",
    lender: "",
    loan_servicer: "",
    notes: "",
    maturity_date: "",
  };
}

// Format US phone as (XXX) XXX-XXXX while typing
function formatUSPhone(raw) {
  const digits = (raw || "").replace(/\D/g, "").slice(0, 10);
  if (digits.length === 0) return "";
  if (digits.length < 4) return `(${digits}`;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function isValidUSPhone(raw) {
  return (raw || "").replace(/\D/g, "").length >= 10;
}

function calcMonthly(principal, annualRate, termYears, loanType = "amortizing", ioMonths = 0, termMonths = 0) {
  const n = Math.max(termYears * 12 + (termMonths || 0), 1);
  const r = annualRate / 100 / 12;
  if (loanType === "interest_only") {
    return r > 0 ? principal * r : 0;
  }
  if (r === 0) return principal / n;
  return (principal * (r * Math.pow(1 + r, n))) / (Math.pow(1 + r, n) - 1);
}

export default function Loans() {
  const { user } = useAuth();
  const moduleDisabled = user?.loans_module_enabled === false;

  const [items, setItems] = useState([]);
  const [properties, setProperties] = useState([]);
  const [alerts, setAlerts] = useState({ alerts: [], counts: { red: 0, yellow: 0, green: 0 }, total: 0 });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultForm());
  const [scheduleFor, setScheduleFor] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [paymentsFor, setPaymentsFor] = useState(null);
  const [calcOpen, setCalcOpen] = useState(false);
  const [refiFor, setRefiFor] = useState(null);
  const [stressFor, setStressFor] = useState(null);
  const [recordPaymentPickerOpen, setRecordPaymentPickerOpen] = useState(false);
  const [borrowersLibOpen, setBorrowersLibOpen] = useState(false);
  const [borrowerPickerOpen, setBorrowerPickerOpen] = useState(false);

  const load = async () => {
    if (moduleDisabled) return;
    const [l, p, a] = await Promise.all([
      api.get("/loans"), api.get("/properties"), api.get("/loans/alerts"),
    ]);
    setItems(l.data);
    setProperties(p.data);
    setAlerts(a.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [moduleDisabled]);

  const openNew = () => { setEditing(null); setForm(defaultForm()); setOpen(true); };
  const openEdit = (loan) => {
    setEditing(loan);
    setForm({
      loan_number: loan.loan_number,
      property_id: loan.property_id || "",
      property_address: loan.property_address,
      loan_date: loan.loan_date,
      loan_amount: loan.loan_amount,
      interest_rate: loan.interest_rate,
      term_years: loan.term_years,
      term_months: loan.term_months || 0,
      loan_type: loan.loan_type || "amortizing",
      io_period_months: loan.io_period_months || 0,
      borrower_name: loan.borrower_name || "",
      borrower_email: loan.borrower_email || "",
      borrower_phone: (loan.borrower_phone || "").replace(/\D/g, ""),
      borrower_id: loan.borrower_id || "",
      lender: loan.lender || "",
      loan_servicer: loan.loan_servicer || "",
      notes: loan.notes || "",
      maturity_date: loan.maturity_date || "",
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (form.borrower_phone && !isValidUSPhone(form.borrower_phone)) {
      toast.error("Borrower phone must be a 10-digit US number");
      return;
    }
    try {
      const payload = {
        ...form,
        loan_amount: Number(form.loan_amount),
        interest_rate: Number(form.interest_rate),
        term_years: Number(form.term_years),
        term_months: Number(form.term_months) || 0,
        io_period_months: Number(form.io_period_months) || 0,
        property_id: form.property_id || null,
        maturity_date: form.maturity_date || null,
      };
      if (editing) {
        await api.put(`/loans/${editing.id}`, payload);
        toast.success("Loan updated");
      } else {
        await api.post("/loans", payload);
        toast.success("Loan added");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this loan?")) return;
    await api.delete(`/loans/${id}`);
    load();
  };

  const viewSchedule = async (loan) => {
    try {
      const { data } = await api.get(`/loans/${loan.id}/schedule`);
      setScheduleFor(data.loan);
      setSchedule(data.schedule);
    } catch (e) {
      toast.error("Could not load schedule");
    }
  };

  // Live preview in dialog form
  const liveMonthly = useMemo(() => {
    const amt = Number(form.loan_amount) || 0;
    const r = Number(form.interest_rate) || 0;
    const t = Number(form.term_years) || 0;
    const tm = Number(form.term_months) || 0;
    if (!amt || (!t && !tm)) return 0;
    return calcMonthly(amt, r, t, form.loan_type, Number(form.io_period_months) || 0, tm);
  }, [form.loan_amount, form.interest_rate, form.term_years, form.term_months, form.loan_type, form.io_period_months]);

  const onPropertyChange = (id) => {
    const p = properties.find((x) => x.id === id);
    setForm({ ...form, property_id: id, property_address: p?.address || form.property_address });
  };

  const totalDebt = items.reduce((s, l) => s + l.loan_amount, 0);
  const totalMonthly = items.reduce((s, l) => s + (l.monthly_payment || 0), 0);

  // Module-disabled branch — placed AFTER all hooks to respect Rules of Hooks.
  if (moduleDisabled) {
    return (
      <DashboardShell>
        <div className="p-8 max-w-2xl">
          <div className="bg-white border border-dashed border-slate-200 rounded-sm p-12 text-center" data-testid="loans-module-disabled">
            <div className="w-12 h-12 mx-auto bg-slate-100 text-slate-400 rounded-sm flex items-center justify-center mb-4">
              <Landmark className="w-6 h-6" />
            </div>
            <div className="overline text-slate-500 mb-2">Loans module disabled</div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{fontFamily:"Outfit"}}>Loans are turned off</h1>
            <p className="text-sm text-slate-500 max-w-md mx-auto mb-6">
              Your existing loan records are safely preserved. Re-enable the module in Settings whenever you want to see them again.
            </p>
            <Link to="/dashboard/settings" className="btn-primary-dark text-sm inline-flex items-center gap-2" data-testid="loans-disabled-go-settings">
              Go to Settings →
            </Link>
          </div>
        </div>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell>
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="overline text-slate-500">Debt Stack</div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{fontFamily:"Outfit"}}>Commercial Loans</h1>
            <p className="text-sm text-slate-500 mt-1">{items.length} active facilities · {currency(totalDebt)} total principal · {currency(totalMonthly)}/mo service</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setCalcOpen(true)} data-testid="open-calculator-button" className="flex items-center gap-2 text-sm font-semibold px-3 py-2 rounded-sm bg-amber-400 hover:bg-amber-500 text-slate-900 border border-amber-500 transition-colors">
              <Calculator className="w-4 h-4" /> Calculator
            </button>
            <button
              onClick={() => {
                if (items.length === 0) {
                  toast.error("Add a loan first");
                  return;
                }
                if (items.length === 1) {
                  setPaymentsFor(items[0]);
                  return;
                }
                setRecordPaymentPickerOpen(true);
              }}
              data-testid="record-loan-payment-button"
              className="flex items-center gap-2 text-sm font-semibold px-3 py-2 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white border border-emerald-700 transition-colors"
            >
              <DollarSign className="w-4 h-4" /> Record Payment
            </button>
            <button onClick={() => setBorrowersLibOpen(true)} data-testid="borrowers-library-button" className="flex items-center gap-2 text-sm font-semibold px-3 py-2 rounded-sm bg-blue-600 hover:bg-blue-700 text-white border border-blue-700 transition-colors">
              <Users className="w-4 h-4" /> Borrowers
            </button>
            <button onClick={openNew} data-testid="add-loan-button" className="btn-primary-dark flex items-center gap-2 text-sm">
              <Plus className="w-4 h-4" /> Add Loan
            </button>
          </div>
        </div>

        <LoansAnalyticsPanel loans={items} />

        <MaturityAlertPanel alerts={alerts} />

        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]" data-testid="loans-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                <Th>Loan #</Th>
                <Th>Property Address</Th>
                <Th>Date</Th>
                <Th className="text-right">Amount</Th>
                <Th className="text-right">Rate</Th>
                <Th className="text-right">Term</Th>
                <Th className="text-right">Monthly</Th>
                <Th>Maturity</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={9} className="text-center py-16 text-slate-400">No loans on the books yet.</td></tr>
              )}
              {items.map((l) => {
                const mat = maturityInfo(l);
                const io = ioTransitionInfo(l);
                // Tint rows by maturity proximity (only when not past-due):
                //   ≤30d → red, ≤60d → yellow, ≤90d → green
                const d = mat?.days_until;
                const matTintRow =
                  d != null && d >= 0
                    ? d <= 30
                      ? "bg-red-50 hover:bg-red-100"
                      : d <= 60
                      ? "bg-yellow-50 hover:bg-yellow-100"
                      : d <= 90
                      ? "bg-green-50 hover:bg-green-100"
                      : "hover:bg-slate-50"
                    : "hover:bg-slate-50";
                return (
                <tr key={l.id} className={`border-b border-slate-100 transition-colors ${matTintRow}`} data-testid={`loan-row-${l.id}`}>
                  <Td className="font-mono text-xs font-bold text-slate-900">{l.loan_number}</Td>
                  <Td className="text-slate-600">{l.property_address}{l.lender && <div className="text-xs text-slate-400">via {l.lender}{l.loan_servicer ? <span className="text-slate-400"> · svc'd by {l.loan_servicer}</span> : null}</div>}{!l.lender && l.loan_servicer && <div className="text-xs text-slate-400">serviced by {l.loan_servicer}</div>}{l.borrower_name && <div className="text-xs text-slate-500 mt-0.5">Borrower: <span className="font-semibold text-slate-700">{l.borrower_name}</span></div>}</Td>
                  <Td className="font-mono text-xs text-slate-500">{l.loan_date ? new Date(l.loan_date + "T00:00:00").toLocaleDateString("en-US") : ""}</Td>
                  <Td className="text-right font-mono font-bold">{currency(l.loan_amount)}</Td>
                  <Td className="text-right font-mono">{l.interest_rate}%</Td>
                  <Td className="text-right font-mono">
                    {l.term_years}y{l.term_months ? ` ${l.term_months}m` : ""}
                    {l.loan_type === "interest_only" && (
                      <div className="text-[10px] uppercase tracking-widest text-amber-700 font-bold">IO</div>
                    )}
                  </Td>
                  <Td className="text-right font-mono font-bold text-slate-900">{currency(l.monthly_payment)}</Td>
                  <Td>
                    <MaturityBadge mat={mat} io={io} />
                  </Td>
                  <Td className="text-right">
                    <button onClick={() => setRefiFor(l)} className="text-slate-500 hover:text-slate-900 p-1 mr-1 text-xs font-semibold" data-testid={`refinance-${l.id}`}>Refi</button>
                    <button onClick={() => setStressFor(l)} className="text-slate-500 hover:text-amber-700 p-1 mr-1" title="Rate-shock stress test (+100/+200/+300 bps)" data-testid={`stress-test-${l.id}`}><TrendingUp className="w-4 h-4" /></button>
                    <button onClick={() => viewSchedule(l)} className="text-slate-500 hover:text-slate-900 p-1 mr-1 text-xs font-semibold" data-testid={`view-schedule-${l.id}`}>Schedule</button>
                    <button onClick={() => setPaymentsFor(l)} className="text-slate-500 hover:text-emerald-600 p-1 mr-1" title="Payments & accounting" data-testid={`payments-loan-${l.id}`}><DollarSign className="w-4 h-4" /></button>
                    <button onClick={() => openEdit(l)} className="text-slate-500 hover:text-slate-900 p-1" data-testid={`edit-loan-${l.id}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(l.id)} className="text-slate-500 hover:text-red-600 p-1 ml-1" data-testid={`delete-loan-${l.id}`}><Trash2 className="w-4 h-4" /></button>
                  </Td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Add/edit loan dialog */}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto p-5">
            <DialogHeader>
              <DialogTitle style={{fontFamily:"Outfit"}} className="text-base">{editing ? "Edit loan" : "Add loan"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={save} className="space-y-2.5 text-sm">
              <div className="grid grid-cols-2 gap-2.5">
                <Field label="Loan number"><input required className="input-clean w-full" data-testid="loan-number-input" value={form.loan_number} onChange={(e)=>setForm({...form, loan_number:e.target.value})} /></Field>
                <Field label="Loan date"><USDateInput required data-testid="loan-date-input" value={form.loan_date} onChange={(iso)=>setForm({...form, loan_date: iso})} /></Field>
              </div>
              <Field label="Maturity date (optional — overrides loan date + term)">
                <USDateInput
                  data-testid="loan-maturity-date-input"
                  value={form.maturity_date}
                  onChange={(iso)=>setForm({...form, maturity_date: iso})}
                />
                <div className="text-[10px] text-slate-500 mt-1 font-mono uppercase tracking-widest">
                  Leave empty to auto-compute from Loan date + term. Set to override for refis, extensions, or assumed loans where the contractual maturity differs.
                </div>
              </Field>
              <Field label="Link property (optional)">
                <select className="input-clean w-full" data-testid="loan-property-select" value={form.property_id} onChange={(e) => onPropertyChange(e.target.value)}>
                  <option value="">— No link — enter address below</option>
                  {properties.map((p) => <option key={p.id} value={p.id}>{p.name} — {p.address}</option>)}
                </select>
              </Field>
              <Field label="Property address"><input required className="input-clean w-full" data-testid="loan-address-input" value={form.property_address} onChange={(e)=>setForm({...form, property_address:e.target.value})} /></Field>
              <div className="grid grid-cols-4 gap-2.5">
                <Field label="Loan amount"><input required type="text" inputMode="decimal" className="input-clean w-full font-mono" data-testid="loan-amount-input" value={formatUSD(form.loan_amount)} onChange={(e)=>setForm({...form, loan_amount:parseUSD(e.target.value)})} placeholder="$0" /></Field>
                <Field label="Interest rate (%)"><input required type="number" step="0.01" className="input-clean w-full" data-testid="loan-rate-input" value={form.interest_rate} onChange={(e)=>setForm({...form, interest_rate:e.target.value})} /></Field>
                <Field label="Term (years)"><input required type="number" min="0" className="input-clean w-full" data-testid="loan-term-input" value={form.term_years} onChange={(e)=>setForm({...form, term_years:e.target.value})} /></Field>
                <Field label="Term (months)"><input type="number" min="0" max="11" className="input-clean w-full" data-testid="loan-term-months-input" value={form.term_months} onChange={(e)=>setForm({...form, term_months:e.target.value})} /></Field>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Loan type">
                  <select className="input-clean w-full" data-testid="loan-type-select" value={form.loan_type} onChange={(e)=>setForm({...form, loan_type:e.target.value, io_period_months: e.target.value === "amortizing" ? 0 : form.io_period_months})}>
                    <option value="amortizing">Amortizing (P&I)</option>
                    <option value="interest_only">Interest-only</option>
                  </select>
                </Field>
                {form.loan_type === "interest_only" && (
                  <Field label="IO period (months, 0 = full term + balloon)">
                    <input type="number" min="0" className="input-clean w-full" data-testid="loan-io-period-input" value={form.io_period_months} onChange={(e)=>setForm({...form, io_period_months:e.target.value})} />
                  </Field>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2.5">
                <Field label="Lender"><input className="input-clean w-full" data-testid="loan-lender-input" value={form.lender} onChange={(e)=>setForm({...form, lender:e.target.value})} placeholder="e.g. KeyBank N.A." /></Field>
                <Field label="Loan Servicer"><input className="input-clean w-full" data-testid="loan-servicer-input" value={form.loan_servicer} onChange={(e)=>setForm({...form, loan_servicer:e.target.value})} placeholder="e.g. KeyBank Real Estate Capital" /></Field>
              </div>

              <div className="border-t border-slate-200 pt-2.5">
                <div className="flex items-center justify-between mb-2">
                  <div className="overline text-slate-600">Borrower</div>
                  <div className="flex items-center gap-2">
                    {form.borrower_id && (
                      <span className="text-[10px] bg-emerald-50 text-emerald-700 font-semibold px-1.5 py-0.5 rounded-sm flex items-center gap-1" data-testid="borrower-linked-chip">
                        <Users className="w-3 h-3" /> Linked
                        <button type="button" onClick={() => setForm({...form, borrower_id: ""})} className="text-emerald-700 hover:text-emerald-900 ml-0.5" title="Unlink from library" data-testid="borrower-unlink-btn">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    )}
                    <button type="button" onClick={() => setBorrowerPickerOpen(true)} className="text-[11px] text-indigo-600 hover:text-indigo-800 font-semibold flex items-center gap-1" data-testid="pick-borrower-btn">
                      <Users className="w-3 h-3" /> Pick from library
                    </button>
                  </div>
                </div>
                <Field label="Borrower name">
                  <input
                    className="input-clean w-full"
                    data-testid="loan-borrower-name-input"
                    value={form.borrower_name}
                    onChange={(e)=>setForm({...form, borrower_name:e.target.value, borrower_id: ""})}
                    placeholder="e.g. Acme Holdings LLC"
                  />
                </Field>
                <div className="grid grid-cols-2 gap-2.5 mt-2">
                  <Field label="Email address">
                    <input
                      type="email"
                      className="input-clean w-full"
                      data-testid="loan-borrower-email-input"
                      value={form.borrower_email}
                      onChange={(e)=>setForm({...form, borrower_email:e.target.value})}
                      placeholder="borrower@company.com"
                    />
                  </Field>
                  <Field label="Phone (US)">
                    <USPhoneInput
                      data-testid="loan-borrower-phone-input"
                      value={form.borrower_phone}
                      onChange={(digits) => setForm({...form, borrower_phone: digits})}
                    />
                  </Field>
                </div>
              </div>
              <Field label="Notes"><textarea rows={2} className="input-clean w-full" data-testid="loan-notes-input" value={form.notes} onChange={(e)=>setForm({...form, notes:e.target.value})} /></Field>

              <div className="bg-slate-50 border border-slate-200 p-3 rounded-sm">
                <div className="overline text-slate-500 mb-1">
                  {form.loan_type === "interest_only" ? "Estimated IO Payment" : "Estimated Monthly Payment"}
                </div>
                <div className="flex items-baseline justify-between">
                  <div className="text-2xl font-bold font-mono text-slate-900">{currency(liveMonthly)}<span className="text-xs text-slate-500 font-normal">/mo</span></div>
                  <div className="text-[11px] text-slate-500 font-mono">{form.term_years}y{Number(form.term_months) ? ` ${form.term_months}m` : ""} · {form.interest_rate}% APR</div>
                </div>
                {form.loan_type === "interest_only" && (
                  <div className="mt-1.5 text-[11px] text-amber-700">
                    {Number(form.io_period_months) > 0
                      ? `Interest-only for ${form.io_period_months} months, then amortizing P&I.`
                      : `Interest-only entire term, ${currency(Number(form.loan_amount))} balloon at maturity.`}
                  </div>
                )}
              </div>

              <DialogFooter>
                <button type="button" onClick={()=>setOpen(false)} className="btn-outline-dark">Cancel</button>
                <button type="submit" className="btn-primary-dark" data-testid="loan-submit-button">{editing ? "Save" : "Add loan"}</button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Amortization schedule dialog */}
        <Dialog open={!!scheduleFor} onOpenChange={(v) => !v && setScheduleFor(null)}>
          <DialogContent className="max-w-3xl max-h-[85vh]">
            <DialogHeader>
              <DialogTitle style={{fontFamily:"Outfit"}}>
                Amortization · Loan {scheduleFor?.loan_number}
              </DialogTitle>
            </DialogHeader>
            {scheduleFor && (
              <>
                <div className="grid grid-cols-4 gap-3 pb-3 border-b border-slate-200">
                  <Stat label="Monthly" value={currency(scheduleFor.monthly_payment)} />
                  <Stat label="Total paid" value={currency(scheduleFor.total_paid)} />
                  <Stat label="Total interest" value={currency(scheduleFor.total_interest)} danger />
                  <Stat label="Payments" value={scheduleFor.total_payments} />
                </div>
                <div className="overflow-y-auto max-h-[50vh]" data-testid="schedule-table">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-white border-b border-slate-200">
                      <tr>
                        <th className="text-left px-3 py-2 overline text-slate-500">#</th>
                        <th className="text-right px-3 py-2 overline text-slate-500">Payment</th>
                        <th className="text-right px-3 py-2 overline text-slate-500">Principal</th>
                        <th className="text-right px-3 py-2 overline text-slate-500">Interest</th>
                        <th className="text-right px-3 py-2 overline text-slate-500">Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {schedule.map((r) => (
                        <tr key={r.period} className="border-b border-slate-100 font-mono">
                          <td className="px-3 py-1.5">{r.period}</td>
                          <td className="text-right px-3 py-1.5">{currency(r.payment)}</td>
                          <td className="text-right px-3 py-1.5 text-slate-900">{currency(r.principal)}</td>
                          <td className="text-right px-3 py-1.5 text-amber-700">{currency(r.interest)}</td>
                          <td className="text-right px-3 py-1.5">{currency(r.balance)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Standalone calculator */}
        <QuickCalculator
          open={calcOpen}
          onOpenChange={setCalcOpen}
          onUseValues={(v) => {
            setCalcOpen(false);
            setEditing(null);
            setForm({
              ...defaultForm(),
              loan_amount: v.loan_amount,
              interest_rate: v.interest_rate,
              term_years: v.term_years,
              loan_type: v.loan_type,
              io_period_months: Number(v.io_period_months) || 0,
            });
            setTimeout(() => setOpen(true), 50);
          }}
        />

        {/* Refinance scenario modeler */}
        <RefinanceModeler loan={refiFor} open={!!refiFor} onOpenChange={(v) => !v && setRefiFor(null)} />
        <LoanStressTestDialog loan={stressFor} open={!!stressFor} onOpenChange={(v) => !v && setStressFor(null)} />

        {/* Record Payment — loan picker (only shown when portfolio has > 1 loan) */}
        <Dialog open={recordPaymentPickerOpen} onOpenChange={setRecordPaymentPickerOpen}>
          <DialogContent className="max-w-md p-5" data-testid="record-payment-picker-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "Outfit" }} className="text-base flex items-center gap-2">
                <DollarSign className="w-4 h-4" /> Record payment — which loan?
              </DialogTitle>
            </DialogHeader>
            <div className="text-xs text-slate-500 mb-3">
              Pick the loan this payment is for. You can record principal, interest, or a full P&amp;I split.
            </div>
            <div className="max-h-[60vh] overflow-y-auto border border-slate-200 rounded-sm">
              {items.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  onClick={() => { setRecordPaymentPickerOpen(false); setPaymentsFor(l); }}
                  data-testid={`picker-loan-${l.id}`}
                  className="w-full text-left p-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors flex items-start justify-between gap-3"
                >
                  <div className="min-w-0">
                    <div className="font-mono font-bold text-xs text-slate-900">{l.loan_number}</div>
                    <div className="text-sm text-slate-700 truncate">{l.property_address}</div>
                    {l.lender && <div className="text-[10px] text-slate-400 mt-0.5">via {l.lender}{l.loan_servicer ? ` · svc'd by ${l.loan_servicer}` : ""}</div>}
                    {!l.lender && l.loan_servicer && <div className="text-[10px] text-slate-400 mt-0.5">serviced by {l.loan_servicer}</div>}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono font-bold text-sm text-slate-900">{currency(l.loan_amount)}</div>
                    <div className="text-[11px] text-slate-500 font-mono">{currency(l.monthly_payment)}/mo</div>
                  </div>
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
        <LoanPaymentsDialog loan={paymentsFor} open={!!paymentsFor} onOpenChange={(v) => !v && setPaymentsFor(null)} />

        {/* Borrowers library (management view) */}
        <BorrowersDialog open={borrowersLibOpen} onOpenChange={setBorrowersLibOpen} />

        {/* Borrower picker inside Add/Edit Loan form */}
        <BorrowersDialog
          open={borrowerPickerOpen}
          onOpenChange={setBorrowerPickerOpen}
          pickerMode={true}
          onBorrowerSelected={(b) => {
            setForm((f) => ({
              ...f,
              borrower_id: b.id,
              borrower_name: b.name || "",
              borrower_email: b.email || "",
              borrower_phone: (b.phone || "").replace(/\D/g, ""),
            }));
            toast.success(`Linked ${b.name}`);
          }}
        />
      </div>
    </DashboardShell>
  );
}

function QuickCalculator({ open, onOpenChange, onUseValues }) {
  const [form, setForm] = useState({
    loan_amount: 500000,
    interest_rate: 6.5,
    term_value: 25,
    term_unit: "years", // "years" | "months"
    loan_type: "amortizing",
    io_period_months: 0,
    // Optional purchase-underwriting inputs — drive the green/red approval
    // chip. When NOI or purchase price is zero/blank, no judgement is shown.
    noi: 0,                 // Annual Net Operating Income ($)
    purchase_price: 0,      // Purchase Price ($)
  });

  const termMonths = form.term_unit === "months"
    ? Math.max(Number(form.term_value), 1)
    : Math.max(Number(form.term_value) * 12, 1);
  const termYearsFloat = termMonths / 12;

  const monthly = useMemo(() => {
    // calcMonthly takes years — pass fractional years to get correct month count
    return calcMonthly(
      Number(form.loan_amount),
      Number(form.interest_rate),
      termYearsFloat,
      form.loan_type,
      Number(form.io_period_months) || 0,
    );
  }, [form, termYearsFloat]);

  const n = termMonths;
  const principal = Number(form.loan_amount);
  const isIO = form.loan_type === "interest_only";
  const ioMonths = Number(form.io_period_months) || 0;
  const total = isIO
    ? (ioMonths && ioMonths < n
        ? monthly * ioMonths + (() => {
            const r = Number(form.interest_rate) / 100 / 12;
            const rem = n - ioMonths;
            const pi = r > 0 ? (principal * (r * Math.pow(1 + r, rem))) / (Math.pow(1 + r, rem) - 1) : principal / rem;
            return pi * rem;
          })()
        : monthly * n + principal /* full-IO + balloon */)
    : monthly * n;
  const interest = total - principal;

  // Lossless-when-years conversion for "Add loan with these values"
  const equivYearsForLoan = form.term_unit === "months"
    ? Math.max(Math.round(termMonths / 12), 1)
    : Math.max(Number(form.term_value), 1);
  const roundingLoss = form.term_unit === "months" && termMonths % 12 !== 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle style={{fontFamily:"Outfit"}}>Mortgage Calculator</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Amount ($)"><input type="number" className="input-clean w-full" data-testid="calc-amount" value={form.loan_amount} onChange={(e)=>setForm({...form, loan_amount:e.target.value})} /></Field>
            <Field label="Rate (%)"><input type="number" step="0.01" className="input-clean w-full" data-testid="calc-rate" value={form.interest_rate} onChange={(e)=>setForm({...form, interest_rate:e.target.value})} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label={`Term (${form.term_unit === "months" ? "months" : "years"})`}>
              <input type="number" min="1" className="input-clean w-full" data-testid="calc-term" value={form.term_value} onChange={(e)=>setForm({...form, term_value:e.target.value})} />
            </Field>
            <Field label="Term unit">
              <select className="input-clean w-full" data-testid="calc-term-unit" value={form.term_unit} onChange={(e)=>{
                const next = e.target.value;
                // Auto-convert so the monthly payment stays the same
                setForm((f) => ({
                  ...f,
                  term_unit: next,
                  term_value: next === "months"
                    ? String(Math.round((Number(f.term_value) || 0) * 12))
                    : String(Math.max(Math.round((Number(f.term_value) || 0) / 12), 1)),
                }));
              }}>
                <option value="years">Years</option>
                <option value="months">Months</option>
              </select>
            </Field>
          </div>
          <div className="text-xs text-slate-500 font-mono">
            = {termMonths} months · {(termMonths / 12).toFixed(2)} years · {n} payments
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type">
              <select className="input-clean w-full" data-testid="calc-type" value={form.loan_type} onChange={(e)=>setForm({...form, loan_type:e.target.value, io_period_months: e.target.value === "amortizing" ? 0 : form.io_period_months})}>
                <option value="amortizing">Amortizing</option>
                <option value="interest_only">Interest-only</option>
              </select>
            </Field>
            {isIO && (
              <Field label="IO months (0 = full term + balloon)">
                <input type="number" min="0" className="input-clean w-full" data-testid="calc-io-period" value={form.io_period_months} onChange={(e)=>setForm({...form, io_period_months:e.target.value})} />
              </Field>
            )}
          </div>

          {/* Purchase underwriting — optional, drives the approval chip */}
          <div className="border-t border-slate-200 pt-3">
            <div className="overline text-slate-600 mb-2">Purchase underwriting (optional)</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Annual NOI ($)">
                <input
                  type="text"
                  inputMode="decimal"
                  className="input-clean w-full font-mono"
                  data-testid="calc-noi"
                  value={formatUSD(form.noi)}
                  onChange={(e) => setForm({ ...form, noi: parseUSD(e.target.value) })}
                  placeholder="$0"
                />
              </Field>
              <Field label="Purchase price ($)">
                <input
                  type="text"
                  inputMode="decimal"
                  className="input-clean w-full font-mono"
                  data-testid="calc-purchase-price"
                  value={formatUSD(form.purchase_price)}
                  onChange={(e) => setForm({ ...form, purchase_price: parseUSD(e.target.value) })}
                  placeholder="$0"
                />
              </Field>
            </div>
            {(() => {
              const noi = Number(form.noi) || 0;
              const price = Number(form.purchase_price) || 0;
              const rate = Number(form.interest_rate) || 0;
              const capRate = price > 0 ? (noi / price) * 100 : null;
              const annualDebt = monthly * 12;
              const dscr = annualDebt > 0 ? noi / annualDebt : null;
              const hasInputs = noi > 0 && price > 0;
              const spread = capRate != null ? capRate - rate : null;
              const good = hasInputs && capRate != null && spread != null && spread > 0 && (dscr == null || dscr >= 1.25);
              const bad = hasInputs && !good;
              return (
                <div className="mt-3 grid grid-cols-2 gap-3" data-testid="calc-cap-rate-row">
                  <div className="bg-slate-100 border border-slate-200 rounded-sm p-3">
                    <div className="overline text-slate-600">Cap rate</div>
                    <div
                      data-testid="calc-cap-rate-value"
                      className={`mt-1 text-2xl font-bold font-mono tabular-nums ${
                        capRate == null ? "text-slate-400" :
                        spread > 0 ? "text-emerald-700" :
                        "text-rose-700"
                      }`}
                    >
                      {capRate != null ? `${capRate.toFixed(2)}%` : "—"}
                    </div>
                    {capRate != null && (
                      <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 mt-1">
                        {spread > 0
                          ? `+${spread.toFixed(2)}% spread vs rate`
                          : `${spread.toFixed(2)}% — negative leverage`}
                        {dscr != null && ` · DSCR ${dscr.toFixed(2)}x`}
                      </div>
                    )}
                  </div>
                  <div className="flex items-stretch">
                    {good && (
                      <button
                        type="button"
                        disabled
                        data-testid="calc-approval-good"
                        className="w-full bg-emerald-600 text-white font-bold rounded-sm flex items-center justify-center gap-2 px-4 py-3 cursor-default"
                        title={`Cap rate (${capRate.toFixed(2)}%) exceeds the interest rate (${rate.toFixed(2)}%) — positive leverage${dscr != null ? `, DSCR ${dscr.toFixed(2)}x ≥ 1.25x` : ""}.`}
                      >
                        <ShieldCheck className="w-4 h-4" /> Good Purchase
                      </button>
                    )}
                    {bad && (
                      <button
                        type="button"
                        disabled
                        data-testid="calc-approval-bad"
                        className="w-full bg-rose-600 text-white font-bold rounded-sm flex items-center justify-center gap-2 px-4 py-3 cursor-default"
                        title={
                          spread != null && spread <= 0
                            ? `Cap rate (${capRate.toFixed(2)}%) is ≤ interest rate (${rate.toFixed(2)}%) — negative leverage.`
                            : `DSCR ${dscr != null ? dscr.toFixed(2) : "?"}x is below the 1.25x bankable threshold.`
                        }
                      >
                        <AlertTriangle className="w-4 h-4" /> Poor Purchase
                      </button>
                    )}
                    {!hasInputs && (
                      <div className="w-full bg-slate-50 border border-dashed border-slate-300 text-slate-500 text-xs rounded-sm flex items-center justify-center text-center px-4 py-3">
                        Enter NOI + purchase price to see the deal verdict.
                      </div>
                    )}
                  </div>
                  {bad && noi > 0 && rate > 0 && (() => {
                    // Recommended max purchase price = price at which cap rate
                    // matches the loan rate + a 50bps "positive leverage" buffer.
                    // Pricing at the buffered cap rate gets the deal back into
                    // green-button territory (assuming DSCR is met by the same).
                    const targetCap = rate + 0.5; // buffer beyond pure break-even
                    const breakEvenPrice = noi / (rate / 100);
                    const recommendedPrice = noi / (targetCap / 100);
                    const discountPct = price > 0 ? ((price - recommendedPrice) / price) * 100 : null;
                    return (
                      <div
                        data-testid="calc-recommended-price"
                        className="col-span-2 mt-3 bg-amber-50 border border-amber-200 rounded-sm p-3 text-sm"
                      >
                        <div className="font-bold text-amber-900 flex items-center gap-1.5">
                          <ShieldCheck className="w-4 h-4" /> Recommended max purchase price
                        </div>
                        <div className="mt-1.5 font-mono tabular-nums text-2xl text-amber-900">
                          {currency(Math.round(recommendedPrice))}
                        </div>
                        <div className="mt-1 text-[11px] uppercase tracking-widest font-mono text-amber-800">
                          Targets a {targetCap.toFixed(2)}% cap (+50bps over the loan rate of {rate.toFixed(2)}%).
                          {discountPct != null && discountPct > 0 && (
                            <> Negotiate <b>{discountPct.toFixed(1)}%</b> off asking ({currency(Math.round(price - recommendedPrice))} reduction).</>
                          )}
                        </div>
                        <div className="mt-1.5 text-[10px] font-mono text-amber-700/80">
                          Break-even (cap = rate): {currency(Math.round(breakEvenPrice))} · paying more = negative leverage.
                        </div>
                      </div>
                    );
                  })()}
                </div>
              );
            })()}
          </div>

          <div className="bg-slate-900 text-white rounded-sm p-6" data-testid="calc-result">
            <div className="overline text-white/60">{isIO ? "Monthly Interest-Only Payment" : "Monthly Payment"}</div>
            <div className="text-5xl font-bold font-mono tracking-tight mt-2">{currency(monthly)}</div>
            <div className="mt-5 grid grid-cols-2 gap-4 pt-4 border-t border-white/10">
              <div>
                <div className="overline text-white/60">Total paid</div>
                <div className="font-mono font-bold mt-1">{currency(total)}</div>
              </div>
              <div>
                <div className="overline text-white/60">Total interest</div>
                <div className="font-mono font-bold mt-1 text-amber-300">{currency(interest)}</div>
              </div>
            </div>
            {isIO && ioMonths === 0 && (
              <div className="mt-3 text-xs text-amber-300">Balloon of {currency(principal)} due at maturity.</div>
            )}
          </div>
          {onUseValues && (
            <div className="pt-2">
              {roundingLoss && (
                <div className="mb-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2">
                  Note: loans are stored in whole years. {termMonths} months will be saved as {equivYearsForLoan} year(s) — the monthly payment in the loan record may differ slightly. Edit the term after saving if needed.
                </div>
              )}
              <div className="flex justify-end">
                <button
                  type="button"
                  data-testid="calc-add-loan-button"
                  onClick={() => onUseValues({
                    loan_amount: form.loan_amount,
                    interest_rate: form.interest_rate,
                    term_years: equivYearsForLoan,
                    loan_type: form.loan_type,
                    io_period_months: form.io_period_months,
                  })}
                  className="btn-primary-dark flex items-center gap-2 text-sm"
                >
                  <Plus className="w-4 h-4" /> Add loan with these values
                </button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, danger }) {
  return (
    <div>
      <div className="overline text-slate-500">{label}</div>
      <div className={`mt-1 font-mono font-bold ${danger ? "text-amber-700" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}

function MaturityBadge({ mat, io }) {
  const pieces = [];
  if (mat) {
    const sev = severityFor(mat.days_until);
    if (sev) {
      const s = SEVERITY_STYLES[sev];
      pieces.push(
        <span key="mat" data-testid={`maturity-badge-${sev}`} className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border ${s.border} ${s.bg} ${s.text} text-[11px] font-bold font-mono`}>
          <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`}></span>
          {mat.days_until}d · {mat.maturity_date ? new Date(mat.maturity_date + "T00:00:00").toLocaleDateString("en-US") : ""}
        </span>
      );
    } else {
      pieces.push(<span key="mat" className="text-xs text-slate-500 font-mono">{mat.maturity_date ? new Date(mat.maturity_date + "T00:00:00").toLocaleDateString("en-US") : ""}</span>);
    }
  }
  if (io) {
    const sev = severityFor(io.days_until);
    if (sev) {
      const s = SEVERITY_STYLES[sev];
      pieces.push(
        <span key="io" data-testid={`io-transition-badge-${sev}`} className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border ${s.border} ${s.bg} ${s.text} text-[11px] font-bold font-mono mt-1`}>
          <Clock className="w-3 h-3" />
          IO flip · {io.days_until}d
        </span>
      );
    }
  }
  return <div className="flex flex-col items-start gap-1">{pieces}</div>;
}


function Th({ children, className="" }) { return <th className={`px-5 py-3 overline text-slate-600 ${className}`}>{children}</th>; }
function Td({ children, className="" }) { return <td className={`px-5 py-4 ${className}`}>{children}</td>; }
function Field({ label, children }) { return <label className="block"><div className="overline text-slate-600 mb-1">{label}</div>{children}</label>; }
